'use client';

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

import { Button } from './button';
import { SelectField } from './select-field';

export interface PaginationProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

/**
 * Self-contained responsive rules for the two page-number rows below —
 * injected here (not via Tailwind) so this works in any host without
 * Tailwind scanning the package's source. Mirrors the app's `sm` breakpoint
 * (640px): below it only `.ak-pagination-mobile` shows, at/above it only
 * `.ak-pagination-desktop` shows.
 */
const PaginationStyles = (): null => {
  if (typeof document === 'undefined') return null;
  const id = 'ak-sb-pagination-styles';
  if (!document.getElementById(id)) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      .ak-pagination-mobile { display: flex; }
      .ak-pagination-desktop { display: none; }
      @media (min-width: 640px) {
        .ak-pagination-mobile { display: none; }
        .ak-pagination-desktop { display: flex; }
      }
    `;
    document.head.appendChild(s);
  }
  return null;
};

/**
 * Self-contained pagination built on the package's own Button/SelectField
 * primitives (no app-level `@/` imports). Windowed page numbers with ellipses
 * plus an optional page-size selector. Renders a narrower mobile row (at
 * most 5 slots) and a fuller desktop row (at most 7 slots) so it doesn't
 * overflow on small screens.
 */
export function Pagination({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  className,
}: PaginationProps): ReactNode {
  const { t } = useTranslation('ui');
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const desktopPages: (number | null)[] =
    totalPages <= 5
      ? Array.from({ length: totalPages }, (_, i) => i + 1)
      : currentPage <= 3
        ? [1, 2, 3, 4, null, totalPages]
        : currentPage >= totalPages - 2
          ? [1, null, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
          : [1, null, currentPage - 1, currentPage, currentPage + 1, null, totalPages];

  const mobilePages: (number | null)[] =
    totalPages <= 5
      ? Array.from({ length: totalPages }, (_, i) => i + 1)
      : currentPage <= 2
        ? [1, 2, null, totalPages]
        : currentPage >= totalPages - 1
          ? [1, null, totalPages - 1, totalPages]
          : [1, null, currentPage, null, totalPages];

  if (totalPages <= 1 && !onPageSizeChange) return null;

  const pageNav = (pages: (number | null)[], rowClassName: string) => (
    <div
      className={rowClassName}
      style={{
        alignItems: 'center',
        gap: 2,
        maxWidth: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      <Button
        variant="ghost"
        size="icon"
        disabled={currentPage === 1}
        aria-label={t('pagination.previousPage')}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronLeft size={14} />
      </Button>

      {pages.map((page, i) =>
        page === null ? (
          <span
            key={`ellipsis-${i}`}
            style={{
              display: 'inline-flex',
              width: 28,
              height: 28,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MoreHorizontal size={14} />
          </span>
        ) : (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'ghost'}
            size="icon"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ),
      )}

      <Button
        variant="ghost"
        size="icon"
        disabled={currentPage === totalPages}
        aria-label={t('pagination.nextPage')}
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRight size={14} />
      </Button>
    </div>
  );

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 12,
        fontSize: 12,
      }}
    >
      <PaginationStyles />

      {totalPages > 1 && (
        <>
          {pageNav(mobilePages, 'ak-pagination-mobile')}
          {pageNav(desktopPages, 'ak-pagination-desktop')}
        </>
      )}

      {onPageSizeChange && (
        <SelectField
          value={String(pageSize)}
          onChange={(v) => onPageSizeChange(Number(v))}
          ariaLabel={t('pagination.pageSize')}
          options={pageSizeOptions.map((s) => ({
            value: String(s),
            label: t('pagination.perPage', { size: s }),
          }))}
        />
      )}
    </div>
  );
}
